import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import './LiveMonitor.css';

const LiveMonitor = () => {
  const { t } = useTranslation();
  const [time, setTime] = useState(new Date());
  const [panPosition, setPanPosition] = useState(0);

  const cameras = [
    { id: 1, location: t('livemonitor.entrance') || 'Main Entrance', angle: 'Wide Angle View' },
    { id: 2, location: t('livemonitor.parking') || 'Parking Area', angle: 'PTZ Camera' },
    { id: 3, location: t('livemonitor.office') || 'Office Floor', angle: 'Dome Camera' },
    { id: 4, location: t('livemonitor.warehouse') || 'Warehouse', angle: 'Bullet Camera' }
  ];

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const panInterval = setInterval(() => {
      setPanPosition(prev => (prev + 1) % 100);
    }, 50);
    return () => clearInterval(panInterval);
  }, []);

  return (
    <div className="live-monitor">
      <div className="live-monitor__container">
        {/* CCTV Surveillance Grid */}
        <div className="live-monitor__grid">
          {cameras.map((camera, index) => (
            <div 
              key={camera.id}
              className="live-monitor__camera"
            >
              {/* CCTV Overlay Info */}
              <div className="cctv-overlay">
                <div className="cctv-overlay__top">
                  <div className="cctv-overlay__rec">
                    <span className="cctv-overlay__rec-dot"></span>
                    REC
                  </div>
                  <div className="cctv-overlay__datetime">
                    {time.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '/')} {time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                  </div>
                </div>
                
                <div className="cctv-overlay__bottom">
                  <div className="cctv-overlay__cam-id">CAM-{String(camera.id).padStart(2, '0')}</div>
                  <div className="cctv-overlay__location">{camera.location.toUpperCase()}</div>
                  <div className="cctv-overlay__angle">{camera.angle}</div>
                </div>
              </div>

              {/* CCTV Street View Simulation */}
              <div className="cctv-feed" style={{
                backgroundPosition: `${panPosition}% 50%`
              }}>
                {/* Camera Panning Effect */}
                <div className="cctv-feed__pan-overlay"></div>
                
                {/* Surveillance Grid */}
                <div className="cctv-feed__grid"></div>
                
                {/* Camera Movement Indicator */}
                <div className="cctv-feed__movement">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <span className="cctv-feed__movement-text">PTZ ACTIVE</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Customer-Focused Features */}
        <div className="live-monitor__features">
          <div className="live-monitor__feature">
            <div className="live-monitor__feature-icon">📱</div>
            <div className="live-monitor__feature-content">
              <div className="live-monitor__feature-title">Remote Access</div>
              <div className="live-monitor__feature-desc">Watch from anywhere</div>
            </div>
          </div>
          <div className="live-monitor__feature">
            <div className="live-monitor__feature-icon">🔔</div>
            <div className="live-monitor__feature-content">
              <div className="live-monitor__feature-title">Motion Alerts</div>
              <div className="live-monitor__feature-desc">Instant notifications</div>
            </div>
          </div>
          <div className="live-monitor__feature">
            <div className="live-monitor__feature-icon">💾</div>
            <div className="live-monitor__feature-content">
              <div className="live-monitor__feature-title">Cloud Storage</div>
              <div className="live-monitor__feature-desc">30 days backup</div>
            </div>
          </div>
          <div className="live-monitor__feature">
            <div className="live-monitor__feature-icon">🌙</div>
            <div className="live-monitor__feature-content">
              <div className="live-monitor__feature-title">Night Vision</div>
              <div className="live-monitor__feature-desc">Clear in darkness</div>
            </div>
          </div>
        </div>

        {/* Professional Stats Display */}
        <div className="live-monitor__stats">
          <div className="live-monitor__stat">
            <span className="live-monitor__stat-value">4K/HD</span>
            <span className="live-monitor__stat-label">Quality</span>
          </div>
          <div className="live-monitor__stat">
            <span className="live-monitor__stat-value">99.9%</span>
            <span className="live-monitor__stat-label">Uptime</span>
          </div>
          <div className="live-monitor__stat">
            <span className="live-monitor__stat-value">24/7</span>
            <span className="live-monitor__stat-label">Recording</span>
          </div>
          <div className="live-monitor__stat">
            <span className="live-monitor__stat-value">∞</span>
            <span className="live-monitor__stat-label">Peace of Mind</span>
          </div>
        </div>

        {/* Enhanced CTA */}
        <div className="live-monitor__cta">
          <div className="live-monitor__cta-content">
            <h3 className="live-monitor__cta-title">
              {t('livemonitor.cta') || 'Your Safety, Our Priority'}
            </h3>
            <p className="live-monitor__cta-text">
              Professional installation • 1-year warranty • Free site survey • 24/7 support
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMonitor;
